import { reactive } from 'vue';

/** Chrome's and Edge's install offer; not in the DOM typings yet. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const standaloneQuery = window.matchMedia('(display-mode: standalone)');

/**
 * The console as an installed app. Android and desktop Chrome offer to install
 * (canPrompt); iPhone and iPad only install from Safari's Share menu, and only an
 * installed app may receive push there — hence `ios` and `standalone`.
 */
export const pwa = reactive({
  ios:
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
  standalone:
    standaloneQuery.matches || (navigator as { standalone?: boolean }).standalone === true,
  canPrompt: false,
});

let offer: InstallPromptEvent | null = null;

export function startPwa(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    offer = e as InstallPromptEvent;
    pwa.canPrompt = true;
  });
  window.addEventListener('appinstalled', () => {
    offer = null;
    pwa.canPrompt = false;
  });
  standaloneQuery.addEventListener('change', (e) => (pwa.standalone = e.matches));
  // Not in development: a worker would outlive Vite's reloads and confuse them.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    });
  }
}

/** Shows the browser's install dialog; true when the user accepted. */
export async function installApp(): Promise<boolean> {
  const e = offer;
  if (!e) return false;
  offer = null;
  pwa.canPrompt = false;
  await e.prompt();
  return (await e.userChoice).outcome === 'accepted';
}
